import camelCase from 'lodash/camelCase';
import { SESSION_TIMEOUT_ERROR } from '@xmatters/common/constants/commonStatus';
import { checkStatus } from '@xmatters/common/utils/http';
import { callXmApi, isNewXmApiCall } from '@xmatters/xm-api/xmApiToken';
import { isTimedOut } from '@xmatters/redux/services/sessionService';
import {
  isEBSEmbedded,
  getEBFDInstanceUrl,
} from '@xmatters/common/context/ebsGlobalContext';
import { generateURIWithQueryParams as defaultUriWithParamsGenerator } from '@xmatters/redux/services/util';

// Handle cases where response text is blank
// FIXME: When text exists but is malformatted, this function will throw an unhandled exception.
export function parseJSON(response) {
  return response.text().then(text => (text ? JSON.parse(text) : {}));
}

/**
 * Returns the final uri with query string.
 *
 * @callback uriWithParamsGenerator
 * @param {string} uri
 * @param {Object} queryObj Object containing query parameters
 */

export const callApi = ({
  uri,
  options,
  hasFile,
  contentType = 'application/json',
}) => {
  const headers = {
    Accept: 'application/json',
    'Content-Type': contentType,
  };

  if (hasFile) {
    delete headers['Content-Type'];
  }

  const isXmIntegrationCall = uri.startsWith('/api/integration/1');

  const preppedUri =
    isEBSEmbedded() && isXmIntegrationCall ? getEBFDInstanceUrl() + uri : uri;
  return fetch(preppedUri, {
    credentials: 'same-origin',
    headers,
    // override fetch options
    ...options,
  });
};

/**
 * Generic 'fetch' wrapper for API requests. Parses json automatically.
 * Includes support for handling auth tokens for xm-api.
 * @param {Object} meta - Any 'fetch' options
 * @param {string} meta.uri - The uri to call (mandatory)
 * @param {string} [meta.contentType] - Content type of the request (defaults to "application/json")
 * @param {function} [meta.responseHandler] - An optional responseHandler
 * @param {Object} [meta.options] - Overrides that will be splat on the fetch call
 * @return {Promise}
 */
export function call(meta) {
  const { uri, responseHandler } = meta;

  const fetchPromise = isNewXmApiCall(uri) ? callXmApi(meta) : callApi(meta);
  // override standard response handling if responseHandler present
  if (responseHandler) {
    return fetchPromise.then(responseHandler).catch(responseHandler);
  }
  // standard response handling
  return fetchPromise.then(checkStatus).then(response => parseJSON(response));
}

/**
 * Wrapper function for a 'get' fetch
 * @param {Object} options
 * @param {string} [options.uri]
 * @param {Object} [options.params] Object containing query parameters
 * @param {uriWithParamsGenerator} [options.customUriWithParamsGenerator]
 * @param {Function} [options.responseHandler] Custom response handler
 * @param {Number} [options.retries] number of retries on http 429 or 5XX errors
 * @param {...Object} otherOptions other fetch options (i.e. mode, cache) to override
 * @return {Promise}
 */
export function get({
  uri,
  params,
  customUriWithParamsGenerator,
  isUriEncoded,
  responseHandler,
  retries = 0,
  ...otherOptions
}) {
  const generateUri =
    customUriWithParamsGenerator || defaultUriWithParamsGenerator;
  const uriWithParams = generateUri(uri, params, isUriEncoded);
  // if session is timed out, do not make the call
  if (isTimedOut()) {
    return Promise.reject(new Error(SESSION_TIMEOUT_ERROR));
  }
  return call({
    options: otherOptions,
    uri: uriWithParams,
    responseHandler,
  }).catch(err => {
    // 429 is due to rate limit in xmapi
    if (
      retries > 0 &&
      (err?.response?.status >= 500 || err?.response?.status === 429)
    ) {
      return get({
        uri,
        params,
        customUriWithParamsGenerator,
        isUriEncoded,
        responseHandler,
        retries: retries - 1,
        ...otherOptions,
      });
    }
    throw err;
  });
}

/**
 * Wrapper function for a 'get' fetch that loops over a paged dataset to return
 * the whole dataset on initial pull
 * @param {Object} options
 * @param {string} options.uri
 * @param {Object} [options.params] Object containing query parameters
 * @param {uriWithParamsGenerator} [options.customUriWithParamsGenerator]
 * @param {Function} [options.responseHandler] Custom response handler
 * @return {Promise}
 */
export function getall({
  uri,
  params,
  customUriWithParamsGenerator,
  responseHandler,
  retries = 0,
  isUriEncoded,
}) {
  return get({
    uri,
    params,
    customUriWithParamsGenerator,
    responseHandler,
    retries,
    isUriEncoded,
  }).then(result =>
    // eslint-disable-next-line no-use-before-define
    getAllGivenResult({
      result,
      customUriWithParamsGenerator,
      responseHandler,
      retries,
    }),
  );
}

/**
 * Wrapper function for a 'get' fetch that loops over a paged dataset to return
 * the whole dataset from a result
 * @param {Object} options
 * @param  {Object} options.result Object containing first set of results
 * @param  {Object} [options.params] Object containing query parameters
 * @param  {Function} [options.responseHandler] Custom response handler
 * @param  {uriWithParamsGenerator} [options.customUriWithParamsGenerator]
 * @return {Promise}
 */
function getAllGivenResult({
  customUriWithParamsGenerator,
  responseHandler,
  result,
  retries,
}) {
  if (result?.links?.next) {
    return getall({
      uri: decodeURIComponent(result.links.next),
      customUriWithParamsGenerator,
      responseHandler,
      retries,
    }).then(nextResult => ({
      ...result,
      count: +result.count + +nextResult.count,
      data: Array.isArray(result.data)
        ? result.data.concat(nextResult.data)
        : { ...result.data, ...nextResult.data },
      links: nextResult.links,
    }));
  }
  return result;
}

function updateResultWithAllEmbeds({ result, embeds, embedResults }) {
  return embeds.reduce(
    (newResult, embed, index) => ({
      ...newResult,
      [embed]: embedResults[index],
    }),
    result,
  );
}

/**
 * Wrapper function for a 'get' fetch that loops over a paged dataset to return
 * the whole dataset including all embeds
 * @param {Object} options
 * @param {string} options.uri
 * @param {Object} options.params Object containing query parameters
 * @param {Function} [options.responseHandler] Custom response handler
 * @param {uriWithParamsGenerator} [options.customUriWithParamsGenerator]
 * @return {Promise}
 */
export function getallwithembeds({
  uri,
  params,
  customUriWithParamsGenerator,
  responseHandler,
}) {
  return getall({
    uri,
    params,
    customUriWithParamsGenerator,
    responseHandler,
  }).then(result => {
    if (params.embed) {
      // embeds come back camelCased from xmApi
      const embeds = params.embed.split(',').map(camelCase);

      return Promise.all(
        embeds.map(embed =>
          getAllGivenResult({
            customUriWithParamsGenerator,
            responseHandler,
            result: result[embed],
          }),
        ),
      ).then(embedResults =>
        updateResultWithAllEmbeds({
          result,
          embeds,
          embedResults,
        }),
      );
    }

    return result;
  });
}

/**
 * Wrapper function for a 'post' fetch
 * @param {Object} options
 * @param {string} options.uri
 * @param {Object} options.params Parameters passed in as the body of the post
 * @param {Function} [options.responseHandler] Pass this function if you need more than the response's JSON
 * @param {boolean} [options.isUriEncoded]
 * @param {string} [options.contentType]
 * @return {Promise}
 */
export function post({
  uri,
  params,
  responseHandler,
  isUriEncoded,
  contentType,
}) {
  let body = JSON.stringify(params);
  if (contentType === 'application/x-www-form-urlencoded') {
    body = params;
  }

  if (contentType === 'multipart/form-data') {
    const formData = new FormData();
    Object.keys(params).forEach(key => {
      if (key === 'file' && params?.fileName) {
        formData.append(key, params[key], params.fileName);
      } else {
        formData.append(key, params[key]);
      }
    });

    body = formData;
  }

  return call({
    uri: isUriEncoded ? uri : encodeURI(uri),
    options: {
      method: 'POST',
      body,
    },
    contentType,
    responseHandler,
    hasFile: params?.hasFile,
  });
}

/**
 * Wrapper function for a 'patch' fetch
 * @param {Object} options
 * @param {string} options.uri
 * @param {Object} [options.params] Parameters passed in as the body of the post
 * @param {boolean} [options.isUriEncoded]
 * @param {string} [options.contentType]
 * @return {Promise}
 */
export function patch({ uri, params, isUriEncoded, contentType }) {
  let body = JSON.stringify(params);
  if (contentType && contentType === 'application/x-www-form-urlencoded') {
    body = params;
  }

  return call({
    uri: isUriEncoded ? uri : encodeURI(uri),
    options: {
      method: 'PATCH',
      body,
    },
    contentType,
  });
}

/**
 * Wrapper function for a 'delete' fetch
 * @param {Object} options
 * @param {string} options.uri
 * @param {boolean} [options.isUriEncoded]
 * @return {Promise}
 */
export function del({ uri, isUriEncoded, responseHandler }) {
  return call({
    uri: isUriEncoded ? uri : encodeURI(uri),
    options: {
      method: 'DELETE',
    },
    responseHandler,
  });
}

export function delWithBody({
  uri,
  params,
  responseHandler,
  isUriEncoded,
  contentType,
}) {
  let body = JSON.stringify(params);
  if (contentType === 'application/x-www-form-urlencoded') {
    body = params;
  }

  if (contentType === 'multipart/form-data') {
    const formData = new FormData();
    Object.keys(params).forEach(key => formData.append(key, params[key]));

    body = formData;
  }

  return call({
    uri: isUriEncoded ? uri : encodeURI(uri),
    options: {
      method: 'DELETE',
      body,
    },
    contentType,
    responseHandler,
    hasFile: params?.hasFile,
  });
}

/**
 * Wrapper function for a 'put' fetch
 * @param {Object} options
 * @param {string} options.uri
 * @param {Object} [options.params]
 * @param {boolean} [options.isUriEncoded]
 * @return {Promise}
 */
export function put({ uri, params, isUriEncoded }) {
  return call({
    uri: isUriEncoded ? uri : encodeURI(uri),
    options: {
      method: 'PUT',
      body: JSON.stringify(params),
    },
  });
}

export function postOauthToken({ username, password }) {
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
  };

  const urlencoded = new URLSearchParams();
  urlencoded.append('grant_type', 'password');
  urlencoded.append('client_id', 'ondemand');
  urlencoded.append('username', username);
  urlencoded.append('password', password);

  return fetch('/api/xm/1/oauth2/token', {
    method: 'POST',
    headers,
    body: urlencoded,
    redirect: 'follow',
  });
}
